/** NOTIFICATION_SUBSCRIPTIONS: the booking feed the item creates, read from its `detail()`.
 *
 *  Per account of every restech whose feed is notification subscriptions (Ventrata): one
 *  row per wanted notification type with the row's status, mode, whether it processes,
 *  when it last received a delivery and its last error. Database read-state only — the
 *  vendor is never asked on a render.
 *
 *  The item never deletes a subscription or switches its mode, so anything it cannot
 *  finish (log only, disabled, being deleted, a legacy-webhook conflict) is said here
 *  together with where it is fixed: the Subscriptions page, linked per account. The
 *  item's own Run (execute — it never autoruns) creates what is missing and retries what
 *  the booking system refused.
 *
 *  `detail.accounts` is typed by the spec (`ConsolePreflightNotificationSubscriptionAccount`);
 *  see the backend's `NotificationSubscriptions.detail()`.
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { PreflightFeedAccount, PreflightFeedSubscription, PreflightItem } from '@/api/preflight';
import { HelpLink } from '@/components/HelpLink';
import { subscriptionsHrefForAccount } from '@/features/subscriptions/subscriptionLinks';
import { AccountChip, ModeChip, StatusChip } from '@/features/subscriptions/subscriptionFormat';
import { formatDate, formatRelativeTime } from '@/utils/format';

/** One wanted subscription of one account (`detail.accounts[].subscriptions[]`). */
export type FeedSubscription = PreflightFeedSubscription;

/** One configured account of a feed restech (`detail.accounts[]`). */
export type FeedAccount = PreflightFeedAccount;

export function feedAccountsOf(item: PreflightItem): FeedAccount[] {
  return item.detail.accounts ?? [];
}

/** Rows the item will not finish by itself: they wait for ops on the Subscriptions page. */
function waitsOnOps(s: FeedSubscription): boolean {
  if (!s.exists || s.processing) return false;
  return (
    (s.status === 'active' && s.mode === 'log_only') ||
    s.status === 'disabled' ||
    s.status === 'deleting' ||
    s.status === 'delete_failed'
  );
}

/** What one row means for the feed, in the words of what to do about it. */
export function subscriptionState(s: FeedSubscription): string {
  if (!s.exists) return 'not created';
  if (s.processing) return 'live';
  switch (s.status) {
    case 'active':
      return 'log only — switch it to process on the Subscriptions page';
    case 'pending':
      return 'not confirmed by the booking system — Run retries';
    case 'create_failed':
      return 'refused by the booking system — Run retries';
    case 'disabled':
      return 'disabled — re-enable it on the Subscriptions page';
    case 'deleting':
      return 'being deleted';
    case 'delete_failed':
      return 'delete failed — see the Subscriptions page';
    default:
      return s.status ?? 'unknown';
  }
}

/** The item-level verdict: COMPLETED / PENDING / ERROR, said the way ops act on it. */
function feedVerdict(
  item: PreflightItem,
  accounts: FeedAccount[],
): { severity: 'success' | 'info' | 'warning' | 'error'; text: string } {
  const subs = accounts.flatMap((a) => a.subscriptions);
  switch (item.status) {
    case 'completed':
      return {
        severity: 'success',
        text:
          'The booking feed is live: every subscription is active in process mode, so bookings ' +
          'import as the booking system reports them.',
      };
    case 'started':
      return { severity: 'info', text: 'Creating the subscriptions at the booking system…' };
    case 'error':
      return {
        severity: 'error',
        text:
          "The feed is not complete. Either the booking system refused a subscription, or this tenant's " +
          'own legacy webhooks still deliver the same updates — a process-mode subscription next to them ' +
          'would import every booking twice. The reason is below. Fix it (a legacy webhook is moved on the ' +
          'Subscriptions page), then Run again: a failed run is never retried automatically.',
      };
    default: {
      const waiting = subs.filter(waitsOnOps);
      if (waiting.length > 0) {
        return {
          severity: 'warning',
          text:
            `Waiting on ops: ${waiting.length === 1 ? 'a subscription is' : `${waiting.length} subscriptions are`} ` +
            'in log-only mode, disabled or being deleted. This item never switches a mode or deletes ' +
            'anything — settle it on the Subscriptions page, then Run again.',
        };
      }
      return {
        severity: 'info',
        text: subs.some((s) => s.exists)
          ? 'Run retries the subscriptions the booking system has not confirmed.'
          : 'Not created yet. Run creates the BOOKING_UPDATE and ORDER_UPDATE subscriptions in process ' +
            'mode, for every account with an API key of its own.',
      };
    }
  }
}

function restechName(code: string): string {
  return code ? code.charAt(0).toUpperCase() + code.slice(1) : code;
}

function AccountFeed({ topId, account }: { topId: number; account: FeedAccount }) {
  const href = subscriptionsHrefForAccount(topId, account.restech, account.account || null);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {restechName(account.restech)}
        </Typography>
        <AccountChip account={account.account} />
        <Box sx={{ flexGrow: 1 }} />
        <Link
          component={RouterLink}
          to={href}
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
          Subscriptions page
        </Link>
      </Box>
      {!account.has_key ? (
        <Typography variant="body2" color="text.secondary">
          No API key of its own — nothing is subscribed for this account.
        </Typography>
      ) : account.duplicate_of ? (
        <Typography variant="body2" color="text.secondary">
          Same API key as account {account.duplicate_of}, which is subscribed instead: two
          subscriptions on one key would deliver every booking twice.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={`${restechName(account.restech)} ${account.account || 'default'} subscriptions`}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Processing</TableCell>
                <TableCell>Last received</TableCell>
                <TableCell>Last error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {account.subscriptions.map((s) => (
                <TableRow key={s.type}>
                  <TableCell>
                    {s.id != null ? (
                      <Link component={RouterLink} to={`/subscriptions/${s.id}`}>
                        <code>{s.type}</code>
                      </Link>
                    ) : (
                      <code>{s.type}</code>
                    )}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {subscriptionState(s)}
                    </Typography>
                  </TableCell>
                  <TableCell>{s.status ? <StatusChip status={s.status} /> : '—'}</TableCell>
                  <TableCell>{s.mode ? <ModeChip mode={s.mode} /> : '—'}</TableCell>
                  <TableCell>{s.exists ? (s.processing ? 'yes' : 'no') : '—'}</TableCell>
                  <TableCell>
                    {s.last_received_ts ? (
                      <Tooltip title={formatDate(s.last_received_ts)}>
                        <span>{formatRelativeTime(s.last_received_ts)}</span>
                      </Tooltip>
                    ) : s.exists ? (
                      'never'
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
                    {s.last_error || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export function NotificationSubscriptionsDisplay({ item, topId }: { item: PreflightItem; topId: number }) {
  const accounts = feedAccountsOf(item);
  const verdict = feedVerdict(item, accounts);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Alert severity={verdict.severity} sx={{ mb: 1.5 }} action={<HelpLink story="ventrata-onboarding" newTab />}>
        {verdict.text}
      </Alert>
      {accounts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No account of this tenant takes notification subscriptions.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {accounts.map((a) => (
            <AccountFeed key={`${a.restech}:${a.account}`} topId={topId} account={a} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
